import React, { useEffect, useState } from 'react';
import { Shield, Award, MapPin, Clock, Phone, Mail, Heart } from 'lucide-react';
import { SiteContent } from './admin/types';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const AnimatedCard: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div 
      ref={ref} 
      className={`transition-all duration-500 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const AboutUs: React.FC = () => {
  const [content, setContent] = useState<SiteContent['company']>({
    name: 'BBHA Insurance Services',
    address: '123 Main Street, Anytown, CA 12345',
    phone: '(555) 123-4567',
    email: 'info@bbhainsurance.com',
    description: 'Professional Medicare guidance from licensed independent agents.',
    hours: {
      weekdays: '8:00 AM - 6:00 PM',
      saturday: '9:00 AM - 3:00 PM',
      sunday: 'Closed',
      extended: 'Extended Hours available during enrollment periods'
    }
  });
  const [aboutContent, setAboutContent] = useState<SiteContent['about'] | null>(null);

  useEffect(() => {
    const loadContent = async () => {
      try {
        const response = await fetch('/data/content.json');
        if (response.ok) {
          const data = await response.json();
          if (data.company) {
            setContent(data.company);
          }
          if (data.about) {
            setAboutContent(data.about);
          }
        }
      } catch (error) {
        console.error('Error loading company content:', error);
      }
    };
    loadContent();
  }, []);
  return (
    <section id="about-us" className="py-16 lg:py-24 bg-gradient-to-br from-bbha-cream-50 to-bbha-olive-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-l from-bbha-plum-50/30 to-bbha-olive-50/30"></div>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Header - Enhanced typography */}
        <div className="text-center mb-16 lg:mb-20">
          <h2 id="contact-heading" className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 mb-6 lg:mb-8 leading-tight">
            About {content.name}
          </h2>
          <p className="text-xl sm:text-2xl lg:text-3xl text-gray-600 max-w-4xl mx-auto leading-relaxed font-light">
            {content.description}
          </p>
        </div>

        {/* Our Values Section - Enhanced with tiles */}
        <div className="text-center mb-16 lg:mb-20">
          <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-12 lg:mb-16">
            {aboutContent?.valuesTitle || 'Our Values'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10 max-w-6xl mx-auto">
            <AnimatedCard delay={0}>
              <div className="group bg-bbha-cream-100 rounded-2xl p-8 lg:p-10 shadow-lg hover:shadow-2xl transition-all duration-500 border border-bbha-plum-200 hover:border-bbha-plum-400 transform hover:-translate-y-3">
                <div className="mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300">
                  <div className="p-4 bg-gradient-to-br from-bbha-plum-600 to-bbha-plum-700 rounded-2xl">
                    <Shield className="text-white" size={36} />
                  </div>
                </div>
                <h4 className="text-xl lg:text-2xl xl:text-3xl font-bold text-bbha-plum-600 mb-4 lg:mb-6 group-hover:text-bbha-plum-700 transition-colors duration-300">
                  {aboutContent?.values?.integrity?.title || 'Integrity'}
                </h4>
                <p className="text-lg lg:text-xl text-gray-600 leading-relaxed font-light">
                  {aboutContent?.values?.integrity?.description || 'Honest, transparent guidance you can trust'}
                </p>
              </div>
            </AnimatedCard>
            <AnimatedCard delay={100}>
              <div className="group bg-bbha-cream-100 rounded-2xl p-8 lg:p-10 shadow-lg hover:shadow-2xl transition-all duration-500 border border-bbha-olive-200 hover:border-bbha-olive-400 transform hover:-translate-y-3">
              <div className="mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300">
                <div className="p-4 bg-gradient-to-br from-bbha-olive-600 to-bbha-olive-700 rounded-2xl">
                  <Heart className="text-white" size={36} />
                </div>
              </div>
              <h4 className="text-xl lg:text-2xl xl:text-3xl font-bold text-bbha-olive-600 mb-4 lg:mb-6 group-hover:text-bbha-olive-700 transition-colors duration-300">
                {aboutContent?.values?.service?.title || 'Service'}
              </h4>
              <p className="text-lg lg:text-xl text-gray-600 leading-relaxed font-light">
                {aboutContent?.values?.service?.description || 'Putting your needs first, always'}
              </p>
              </div>
            </AnimatedCard>
            <AnimatedCard delay={200}>
              <div className="group bg-bbha-cream-100 rounded-2xl p-8 lg:p-10 shadow-lg hover:shadow-2xl transition-all duration-500 border border-bbha-plum-200 hover:border-bbha-plum-400 transform hover:-translate-y-3">
              <div className="mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300">
                <div className="p-4 bg-gradient-to-br from-bbha-plum-600 to-bbha-plum-700 rounded-2xl">
                  <Award className="text-white" size={36} />
                </div>
              </div>
              <h4 className="text-xl lg:text-2xl xl:text-3xl font-bold text-bbha-plum-600 mb-4 lg:mb-6 group-hover:text-bbha-plum-700 transition-colors duration-300">
                {aboutContent?.values?.excellence?.title || 'Excellence'}
              </h4>
              <p className="text-lg lg:text-xl text-gray-600 leading-relaxed font-light">
                {aboutContent?.values?.excellence?.description || 'Delivering the highest quality service'}
              </p>
              </div>
            </AnimatedCard>
            <AnimatedCard delay={300}>
              <div className="group bg-bbha-cream-100 rounded-2xl p-8 lg:p-10 shadow-lg hover:shadow-2xl transition-all duration-500 border border-bbha-olive-200 hover:border-bbha-olive-400 transform hover:-translate-y-3">
              <div className="mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300">
                <div className="p-4 bg-gradient-to-br from-bbha-olive-600 to-bbha-olive-700 rounded-2xl">
                  <Clock className="text-white" size={36} />
                </div>
              </div>
              <h4 className="text-xl lg:text-2xl xl:text-3xl font-bold text-bbha-olive-600 mb-4 lg:mb-6 group-hover:text-bbha-olive-700 transition-colors duration-300">
                {aboutContent?.values?.availability?.title || 'Availability'}
              </h4>
              <p className="text-lg lg:text-xl text-gray-600 leading-relaxed font-light">
                {aboutContent?.values?.availability?.description || 'Here when you need us most'}
              </p>
              </div>
            </AnimatedCard>
          </div>
        </div>

        {/* Contact Section - Enhanced */}
        <AnimatedCard>
          <div className="bg-gradient-to-r from-bbha-cream-100 to-bbha-olive-100 rounded-3xl p-8 lg:p-16 shadow-xl">
            <div className="text-center">
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-12 lg:mb-16">
                Get In Touch
              </h3>
            
            {/* Contact Methods - Enhanced Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 mb-12 lg:mb-16 max-w-5xl mx-auto">
              <a 
                href={`tel:${content.phone}`}
                className="group bg-bbha-cream-100 border border-bbha-plum-200 rounded-2xl p-8 lg:p-10 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 min-h-[200px] flex flex-col justify-center"
              >
                <div className="mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300">
                  <div className="p-4 bg-gradient-to-br from-bbha-plum-600 to-bbha-plum-700 rounded-2xl">
                    <Phone className="text-white" size={32} />
                  </div>
                </div>
                <h4 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">Call Us</h4>
                <p className="text-lg lg:text-xl text-bbha-plum-600 font-semibold">{content.phone}</p>
              </a>
              
              <a 
                href={`mailto:${content.email}`}
                className="group bg-bbha-cream-100 border border-bbha-olive-200 rounded-2xl p-8 lg:p-10 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 min-h-[200px] flex flex-col justify-center"
              >
                <div className="mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300">
                  <div className="p-4 bg-gradient-to-br from-bbha-olive-600 to-bbha-olive-700 rounded-2xl">
                    <Mail className="text-white" size={32} />
                  </div>
                </div>
                <h4 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">Email Us</h4>
                <p className="text-lg lg:text-xl text-bbha-olive-600 font-semibold break-all">{content.email}</p>
              </a>
              
              <div className="bg-bbha-cream-100 border border-bbha-plum-200 rounded-2xl p-8 lg:p-10 shadow-lg min-h-[200px] flex flex-col justify-center sm:col-span-2 lg:col-span-1">
                <div className="mb-6 flex justify-center">
                  <div className="p-4 bg-gradient-to-br from-bbha-plum-600 to-bbha-plum-700 rounded-2xl">
                    <MapPin className="text-white" size={32} />
                  </div>
                </div>
                <h4 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">Located In</h4>
                <p className="text-lg lg:text-xl text-bbha-plum-600 font-semibold text-center leading-relaxed">{content.address}</p>
              </div>
            </div>

            
            {/* Office Hours - Enhanced */}
            <div className="max-w-lg mx-auto bg-bbha-cream-100 border border-bbha-olive-200 rounded-2xl p-8 lg:p-10 shadow-lg">
              <h4 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 lg:mb-8 flex items-center justify-center gap-3">
                <div className="p-2 bg-bbha-olive-600 rounded-full">
                  <Clock className="text-white" size={24} />
                </div>
                Office Hours
              </h4>
              <div className="space-y-4 text-lg lg:text-xl">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">Monday - Friday:</span>
                  <span className="font-bold text-right">{content.hours?.weekdays || '8:00 AM - 6:00 PM'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">Saturday:</span>
                  <span className="font-bold text-right">{content.hours?.saturday || '9:00 AM - 3:00 PM'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">Sunday:</span>
                  <span className="font-bold text-right">{content.hours?.sunday || 'Closed'}</span>
                </div>
              </div>
              <div className="mt-6 p-4 bg-bbha-olive-100 rounded-xl">
                <p className="text-lg lg:text-xl text-bbha-olive-800 font-medium text-center leading-relaxed">
                  {content.hours?.extended || 'Extended Hours available during enrollment periods'}
                </p>
              </div>
            </div>
            </div>
          </div>
        </AnimatedCard>

      </div>
    </section>
  );
};

export default AboutUs;
